/**
 * ATP Record Service
 * Înregistrări ATP — cerere + rezultat într-un singur document.
 * ID atomic server-side via Firestore transaction.
 */

import { db } from '../firebase';
import {
  collection, doc, addDoc, getDocs,
  query, where, orderBy, serverTimestamp, runTransaction,
  limit as limitQuery
} from 'firebase/firestore';

const COLLECTION = 'atp_records';
const SEQ_COLLECTION = 'org_sequences';

async function getNextAtpId(organizationId) {
  const seqRef = doc(db, SEQ_COLLECTION, organizationId);
  return await runTransaction(db, async (transaction) => {
    const seqSnap = await transaction.get(seqRef);
    const currentValue = seqSnap.exists() ? (seqSnap.data().atp_next_id || 1) : 1;
    const nextValue = currentValue + 1;
    if (seqSnap.exists()) {
      transaction.update(seqRef, { atp_next_id: nextValue, updated_at: serverTimestamp() });
    } else {
      transaction.set(seqRef, {
        organization_id: organizationId,
        atp_next_id: nextValue,
        lab_next_request_id: 1,
        updated_at: serverTimestamp()
      });
    }
    return currentValue;
  });
}

async function dispatchAtpPositiveEvent(organizationId, atpDocId, atpId, data, userId) {
  try {
    await addDoc(collection(db, 'workflow_events'), {
      event_type: 'ATP_POSITIVE',
      organization_id: organizationId,
      entity_type: 'room',
      entity_id: data.room_id,
      event_data: {
        atp_record_id: atpDocId,
        atp_id: atpId,
        room_id: data.room_id,
        department_id: data.department_id,
        surface_type: data.surface_type_name,
        value: data.value ?? null,
        interpretation: 'neconform'
      },
      source: 'atp_record',
      created_at: serverTimestamp(),
      created_by: userId,
      processed: false
    });
  } catch (err) {
    console.warn('[atpRecordService] dispatchAtpPositiveEvent failed (non-blocking):', err?.message);
  }
}

export async function createATPRecord(organizationId, data, userId) {
  const atpId = await getNextAtpId(organizationId);

  const docData = {
    atp_id: atpId,
    organization_id: organizationId,

    created_at: serverTimestamp(),
    recorded_at: serverTimestamp(),

    department_id: data.department_id || null,
    department_name: data.department_name || '',
    room_id: data.room_id || null,
    room_name: data.room_name || '',
    surface_type_id: data.surface_type_id || null,
    surface_type_name: data.surface_type_name || '',

    operator_id: data.operator_id || userId,
    operator_name: data.operator_name || '',

    value: data.value !== undefined && data.value !== '' ? Number(data.value) : null,
    description: data.description || '',
    interpretation: data.interpretation || 'in_lucru',

    workflow_execution_id: data.workflow_execution_id || null,
    workflow_node_id: data.workflow_node_id || null,

    // Trasabilitate sursă — de unde vine cererea de test ATP
    // SECURITY NOTE: source și source_id sunt scrise de server, nu de client
    source: data.source || 'planned_ipc', // 'planned_ipc' | 'audit' | 'direct_observation' | 'incident' | 'capa' | 'risk_map'
    source_id: data.source_id || null,    // ID obiect sursă: audit_id, capa_id, incident_id, workflow_execution_id
    source_ref: data.source_ref || null,  // Referință human-readable: 'AUD-2025-001', 'CAPA-003'

    created_by: userId,
    updated_at: serverTimestamp(),
    updated_by: userId
  };

  const docRef = await addDoc(collection(db, COLLECTION), docData);

  if (data.interpretation === 'neconform') {
    await dispatchAtpPositiveEvent(organizationId, docRef.id, atpId, data, userId);
  }

  return { id: docRef.id, atp_id: atpId };
}

export async function getATPRecords(organizationId, filters = {}) {
  let q = query(
    collection(db, COLLECTION),
    where('organization_id', '==', organizationId),
    orderBy('created_at', 'desc')
  );
  if (filters.limit) {
    q = query(q, limitQuery(filters.limit));
  }
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getATPRecordsByRoom(organizationId, roomId, days = 30) {
  const q = query(
    collection(db, COLLECTION),
    where('organization_id', '==', organizationId),
    where('room_id', '==', roomId),
    orderBy('created_at', 'desc')
  );
  const snap = await getDocs(q);
  const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  if (!days || days <= 0) return rows;
  const since = new Date();
  since.setDate(since.getDate() - days);
  return rows.filter((r) => {
    const ca = r.created_at?.toDate ? r.created_at.toDate() : (r.created_at ? new Date(r.created_at) : null);
    return ca && ca >= since;
  });
}

export async function getATPNonconformRecords(organizationId) {
  const q = query(
    collection(db, COLLECTION),
    where('organization_id', '==', organizationId),
    where('interpretation', '==', 'neconform'),
    orderBy('created_at', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export const atpRecordService = {
  createATPRecord,
  getATPRecords,
  getATPRecordsByRoom,
  getATPNonconformRecords
};

export default atpRecordService;
